/**
 * Wave C identity kit.
 *
 * ArchetypeEmblem (KLA-040): every archetype gets its own living mark,
 * drawn from its code (the petal count) and its family palette, with
 * the existing glyph at the core. Animates gently, respects reduced
 * motion.
 *
 * ResemblanceCard (KLA-049): famous people who share ONE working trait,
 * named plainly, with the honest disclaimer always visible.
 *
 * ShareArchetypeCard (KLA-047): renders a colourful story (1080x1920)
 * and post (1080x1080) image of the archetype on a canvas, hands them
 * to the phone's share sheet when it exists, downloads them otherwise.
 */
import { useRef, useState } from "react";
import { Share2, Download, Users, Instagram, Loader2 } from "lucide-react";
import { ArchetypeGlyph } from "./ArchetypeGlyph";
import { paletteFor, petalsFor, resemblanceFor, RESEMBLANCE_DISCLAIMER } from "../lib/archetypeIdentity";


export function ArchetypeEmblem({ arch, size = 120 }) {
  if (!arch?.name) return null;
  const pal = paletteFor(arch);
  const petals = petalsFor(arch.code);
  const petalEls = [];
  for (let i = 0; i < petals; i++) {
    const angle = (360 / petals) * i;
    petalEls.push(
      <ellipse
        key={i}
        cx="0" cy="-34" rx="9" ry="20"
        fill="white" opacity="0.3"
        stroke="rgba(255,255,255,0.3)" strokeWidth="0.5"
        transform={`rotate(${angle})`}
      />
    );
  }
  return (
    <div
      className="k-emblem"
      style={{ width: size, height: size }}
      aria-label={`${arch.name} emblem`}
      data-testid="archetype-emblem"
    >
      <svg viewBox="-60 -60 120 120" width={size} height={size}>
        <defs>
          <radialGradient id={`emb-${arch.code}`} cx="35%" cy="30%">
            <stop offset="0%" stopColor={pal.bright} />
            <stop offset="100%" stopColor={pal.deep} />
          </radialGradient>
        </defs>
        <circle r="56" fill={`url(#emb-${arch.code})`} />
        <g className="k-emblem-ring">{petalEls}</g>
        <circle r="26" fill="rgba(255,255,255,0.14)" />
      </svg>
      <span className="k-emblem-glyph" style={{ color: "white" }}>
        <ArchetypeGlyph name={arch.name} />
      </span>
    </div>
  );
}


export function ResemblanceCard({ arch }) {
  const people = resemblanceFor(arch);
  if (!people) return null;
  const pal = paletteFor(arch);
  const bare = (arch.name || "").replace(/^the\s+/i, "").toLowerCase();
  return (
    <div className="k-card mb-8 k-fade-in" data-testid="resemblance-card">
      <div className="k-label mb-1 flex items-center gap-2">
        <Users size={13} /> the same working pattern
      </div>
      <h3 className="klareet-serif mb-4" style={{ fontSize: "1.35rem" }}>
        People who work the way the {bare} works.
      </h3>
      <div className="grid sm:grid-cols-3 gap-4">
        {people.map((p) => (
          <div
            key={p.name}
            className="rounded-2xl p-4"
            style={{ background: pal.wash, border: `1px solid ${pal.bright}44` }}
            data-testid={`resemblance-${p.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
          >
            <div className="font-semibold" style={{ color: "hsl(var(--klareet-ink))" }}>{p.name}</div>
            <div className="k-quiet !text-xs mb-2">{p.field}</div>
            <div className="text-sm" style={{ color: "hsl(var(--klareet-ink-soft))", lineHeight: 1.5 }}>{p.trait}</div>
          </div>
        ))}
      </div>
      <p className="k-quiet !text-xs mt-4" style={{ lineHeight: 1.5 }} data-testid="resemblance-disclaimer">
        {RESEMBLANCE_DISCLAIMER}
      </p>
    </div>
  );
}


// ---- the share images -----------------------------------------------

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawShareImage({ w, h, arch, secondary, honestLine, personName }) {
  const pal = paletteFor(arch);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");

  // Ground: the family gradient.
  const g = ctx.createLinearGradient(0, 0, w * 0.4, h);
  g.addColorStop(0, pal.deep);
  g.addColorStop(1, pal.bright);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);

  // Big soft circles for depth.
  ctx.globalAlpha = 0.14;
  ctx.fillStyle = "#ffffff";
  ctx.beginPath(); ctx.arc(w * 0.85, h * 0.12, w * 0.35, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(w * 0.08, h * 0.9, w * 0.3, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;

  // The petal emblem, drawn big and half off the top.
  const petals = petalsFor(arch.code);
  const cx = w / 2, cy = h * (h > w ? 0.3 : 0.32), R = w * 0.2;
  for (let i = 0; i < petals; i++) {
    const a = (Math.PI * 2 / petals) * i;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(a);
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.ellipse(0, -R, R * 0.28, R * 0.62, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = "#ffffff";
  ctx.beginPath(); ctx.arc(cx, cy, R * 0.5, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = pal.deep;
  ctx.font = `700 ${Math.round(R * 0.42)}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText((arch.code || "K").toUpperCase(), cx, cy + 2);

  // Kicker.
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = `600 ${Math.round(w * 0.028)}px Arial, sans-serif`;
  const kicker = personName ? `${personName.toUpperCase()} IS` : "MY KLAREET ARCHETYPE";
  ctx.fillText(kicker, cx, cy + R + w * 0.075);

  // The name, big.
  ctx.fillStyle = "#ffffff";
  ctx.font = `700 ${Math.round(w * 0.085)}px Georgia, serif`;
  const nameY = cy + R + w * 0.17;
  ctx.fillText(arch.name || "", cx, nameY);
  if (secondary?.name) {
    ctx.font = `400 ${Math.round(w * 0.037)}px Georgia, serif`;
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillText(`with a ${secondary.name.replace(/^The\s+/i, "").toLowerCase()} streak`, cx, nameY + w * 0.075);
  }

  // The honest line, wrapped, inside a soft card.
  const line = (honestLine || "").trim();
  if (line) {
    ctx.font = `400 ${Math.round(w * 0.032)}px Georgia, serif`;
    const maxW = w * 0.74;
    const words = line.split(/\s+/);
    const lines = [];
    let cur = "";
    for (const word of words) {
      const test = cur ? cur + " " + word : word;
      if (ctx.measureText(test).width > maxW && cur) { lines.push(cur); cur = word; }
      else cur = test;
      if (lines.length >= 4) break;
    }
    if (cur && lines.length < 5) lines.push(cur);
    const lh = w * 0.048;
    const boxH = lines.length * lh + w * 0.07;
    const boxY = nameY + w * 0.115;
    ctx.fillStyle = "rgba(255,255,255,0.13)";
    roundRect(ctx, w * 0.09, boxY, w * 0.82, boxH, w * 0.03);
    ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.95)";
    lines.forEach((l, i) => ctx.fillText(l, cx, boxY + w * 0.055 + i * lh));
  }

  // Footer.
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.font = `600 ${Math.round(w * 0.026)}px Arial, sans-serif`;
  ctx.fillText("find yours at klareet.com", cx, h - w * 0.06);

  return canvas;
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

export function ShareArchetypeCard({ report, personName }) {
  const arch = report?.archetype?.primary;
  const secondary = report?.archetype?.secondary;
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");
  const lastUrl = useRef(null);
  if (!arch?.name) return null;
  const pal = paletteFor(arch);

  const make = (kind) => drawShareImage({
    w: 1080,
    h: kind === "story" ? 1920 : 1080,
    arch, secondary,
    honestLine: report?.honest_line,
    personName: personName || "",
  });

  const download = async (kind) => {
    setBusy(kind); setNote("");
    try {
      const blob = await canvasToBlob(make(kind));
      if (lastUrl.current) URL.revokeObjectURL(lastUrl.current);
      const url = URL.createObjectURL(blob);
      lastUrl.current = url;
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-archetype-${kind}.png`;
      a.click();
      setNote("Saved. Post it anywhere: Instagram, Facebook, Snapchat.");
    } catch (_e) {
      setNote("That did not work here. Try the other button, or a screenshot works too.");
    } finally { setBusy(""); }
  };

  const shareSheet = async () => {
    setBusy("share"); setNote("");
    try {
      const blob = await canvasToBlob(make("story"));
      const file = new File([blob], "my-archetype.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Klareet archetype",
          text: `I got ${arch.name}. Find yours at klareet.com`,
        });
        setNote("");
      } else {
        await download("story");
        setNote("Your phone's share sheet is not available here, so the image downloaded instead. Post it anywhere.");
      }
    } catch (_e) {
      setNote(""); // person closed the sheet; not an error
    } finally { setBusy(""); }
  };

  return (
    <div
      className="rounded-2xl p-5 mb-8 k-fade-in"
      style={{ background: `linear-gradient(135deg, ${pal.wash}, hsl(var(--klareet-canvas-2)))`, border: `1px solid ${pal.bright}55` }}
      data-testid="share-archetype-card"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <ArchetypeEmblem arch={arch} size={72} />
        <div className="flex-1 min-w-[220px]">
          <div className="k-label mb-1">show your people</div>
          <h3 className="klareet-serif mb-1" style={{ fontSize: "1.3rem" }}>
            Share your archetype.
          </h3>
          <p className="k-quiet !text-sm mb-4" style={{ lineHeight: 1.5 }}>
            A colourful card of your read, sized for a story or a post. Your answers stay private, only the archetype goes out.
          </p>
          <div className="flex flex-wrap gap-2">
            <button onClick={shareSheet} disabled={!!busy} className="k-btn k-btn-primary !text-sm" data-testid="share-arch-sheet">
              {busy === "share" ? <Loader2 size={14} className="animate-spin" /> : <Share2 size={14} />} Share
            </button>
            <button onClick={() => download("story")} disabled={!!busy} className="k-btn k-btn-ghost !text-sm" data-testid="share-arch-story">
              {busy === "story" ? <Loader2 size={14} className="animate-spin" /> : <Instagram size={14} />} Story size
            </button>
            <button onClick={() => download("post")} disabled={!!busy} className="k-btn k-btn-ghost !text-sm" data-testid="share-arch-post">
              {busy === "post" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Post size
            </button>
          </div>
          {note && <div className="k-quiet !text-xs mt-3" data-testid="share-arch-note">{note}</div>}
        </div>
      </div>
    </div>
  );
}
